/* Aklasu — Product Highlights
 *
 * Video behaviour for editorial highlight blocks:
 *  1. Respect prefers-reduced-motion. If the visitor has reduced
 *     motion enabled, strip autoplay and pause every video. The
 *     poster image stays visible.
 *  2. Pause off-screen videos. Autoplaying every video on the page
 *     is expensive on CPU, GPU, and battery — especially on mobile.
 *     IntersectionObserver pauses videos that aren't in view and
 *     plays them again when they re-enter. The visible video keeps
 *     looping silently.
 *  3. Hard-mute (strict, 2026-07-05). Sound is NEVER allowed:
 *     - muted + defaultMuted + volume 0 on init
 *     - re-muted immediately before every play()
 *     - a volumechange guard re-mutes if ANYTHING (extension,
 *       replay tool, browser quirk) unmutes or raises volume.
 *     Context: session-replay tools (Lucky Orange etc.) rebuild the
 *     <video> in the reviewer's browser and can lose the muted state,
 *     which plays the file's embedded audio track during playback —
 *     reviewers hear music real visitors never heard. The guards below
 *     also cover any real-browser edge case. The definitive fix for
 *     replay audio is uploading video files with the audio track
 *     stripped.
 *
 * The section's image-only and grid layout work without this file;
 * the file only needs to load when a Highlight block uses a video.
 */
(function () {
  'use strict';

  if (window.__akProductHighlightsVideo) return;
  window.__akProductHighlightsVideo = true;

  function hardMute(v) {
    v.muted = true;
    v.defaultMuted = true;
    v.volume = 0;
    v.setAttribute('muted', '');
  }

  function init() {
    var videos = document.querySelectorAll('.ak-ph__video');
    if (!videos.length) return;

    // Strict no-audio enforcement — applies in every path, including
    // reduced-motion, and re-asserts itself if anything unmutes.
    videos.forEach(function (v) {
      hardMute(v);
      if (!v.__akMuteGuard) {
        v.__akMuteGuard = true;
        v.addEventListener('volumechange', function () {
          if (!v.muted || v.volume > 0) hardMute(v);
        });
      }
    });

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function applyReducedMotion() {
      videos.forEach(function (v) {
        v.removeAttribute('autoplay');
        try { v.pause(); } catch (e) {}
      });
    }

    if (reducedMotion.matches) {
      applyReducedMotion();
      return;
    }

    // Pause when off-screen, play when in view. threshold: 0.25 means
    // at least 25% of the video has to be visible before it starts —
    // avoids playing for a frame as the section enters the viewport.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            // Re-assert mute right before playback — play() with sound
            // is never allowed, whatever happened to the element since
            // init. Catch the autoplay rejection silently — common when
            // the tab is backgrounded or the user has site autoplay off.
            hardMute(v);
            var p = v.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          } else {
            try { v.pause(); } catch (e) {}
          }
        });
      }, { threshold: 0.25 });

      videos.forEach(function (v) { io.observe(v); });
    }

    // Listen for live preference changes (system settings change while
    // the page is open). Modern browsers; safe in older too — addEventListener
    // is checked.
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', function (e) {
        if (e.matches) applyReducedMotion();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-init: when the merchant edits a section in the
  // customizer, Shopify re-injects the section HTML. The init flag
  // above prevents double-binding for the page lifecycle, but new
  // video nodes after the editor reload need observers attached. We
  // re-run init on the event without resetting the flag — the new
  // querySelectorAll picks up the newly-injected videos. Existing
  // observers for old videos that no longer exist are garbage
  // collected by the browser when the nodes are removed.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.ak-ph__video')) {
      // Reset the flag specifically so init can run again for the
      // freshly-loaded section's videos.
      window.__akProductHighlightsVideo = false;
      init();
      window.__akProductHighlightsVideo = true;
    }
  });
})();
